import React, { useCallback, useState } from 'react';
import { Clickable, Button } from '@moda/om';
import { useLocation } from 'react-router-dom';
import FavoriteFilledIcon from '@moda/icons/favorite-filled-16';
import FavoriteOutlineIcon from '@moda/icons/favorite-outline-16';
import FavoriteFilledLargeIcon from '@moda/icons/favorite-filled-24';
import FavoriteOutlineLargeIcon from '@moda/icons/favorite-outline-24';
import { useAuthModal } from '../../hooks/useAuthModal';
import { useFavorites, UseFavoritesProps } from './useFavorites';
import { FavoriteConfirmRemove } from './FavoriteConfirmRemove';

import './Favorite.scss';

export interface FavoriteProps extends UseFavoritesProps {
  type: 'BUTTON' | 'ICON';
}

export const Favorite: React.FC<FavoriteProps> = ({ type, variantId }) => {
  const location = useLocation();
  const { user, handleFavorite, isFavorited } = useFavorites({ variantId });
  const { openAuthModal } = useAuthModal();

  const [showConfirmRemove, setShowConfirmRemove] = useState(false);

  const label = isFavorited ? 'Remove from Favorites' : 'Add to Favorites';

  const onClick = useCallback(() => {
    if (!showConfirmRemove && isFavorited && location.pathname === '/favorites') {
      setShowConfirmRemove(true);
    } else if (user.isLoggedIn) {
      handleFavorite();
      setShowConfirmRemove(false);
    } else {
      openAuthModal(handleFavorite);
    }
  }, [
    handleFavorite,
    isFavorited,
    location.pathname,
    openAuthModal,
    showConfirmRemove,
    user.isLoggedIn
  ]);
  const onCloseConfirm = useCallback(() => setShowConfirmRemove(false), [setShowConfirmRemove]);

  return (
    <>
      {type === 'BUTTON' ? (
        <Button aria-label={label} data-testid="favorite-button" onClick={onClick} secondary>
          {isFavorited ? <FavoriteFilledIcon /> : <FavoriteOutlineIcon />}
          {label}
        </Button>
      ) : (
        <Clickable
          aria-label={label}
          className="Favorite__icon"
          data-testid="favorite-icon"
          onClick={onClick}
        >
          {isFavorited ? <FavoriteFilledLargeIcon /> : <FavoriteOutlineLargeIcon />}
        </Clickable>
      )}

      {showConfirmRemove && <FavoriteConfirmRemove onConfirm={onClick} onClose={onCloseConfirm} />}
    </>
  );
};
